import React, {useEffect, useMemo, useState} from "react";
import {withRouter} from "react-router";
import {DemandValidation} from 'constants/DemandValidation';
import {connect} from "react-redux";
import useSearch from "hooks/useSearch";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import Loading from "components/Loading";
import GridBlock from "components/GridBlock";
import Tr from "translations/Translations";
import OfferChoiceService from "services/OfferChoiceService";
import Pagination from "components/Pagination";
import OfferChoiceTable from "./components/OfferChoiceTable";
import Alert from "components/popup/alert/Alert";
import OfferChoiceCancelWhereNoOffersPopup from "./components/OfferChoiceCancelWhereNoOffersPopup";
import OfferChoiceSearch from "./components/OfferChoiceSearch";
import BoldTypography from "components/BoldTypography";
import DemandValidationMessages from "components/DemandValidationMessages";
import OfferChoiceCreateAdditionalRoundPopup from "./components/additionalRound/OfferChoiceCreateAdditionalRoundPopup";
import {RoundType} from "constants/RoundType";
import FinalRealizationValuePopup from "./components/FinalRealizationValuePopup";
import SplitButton, {createButton as createSplitButton} from "components/popup/components/SplitButton";
import MultiButton from "components/popup/components/MultiButton";


function OfferChoice(
    {
        match,
        offerChoiceList,
        offerChoiceJustification,
        demandValidation
    }) {

    const curDemandValidation = DemandValidation.OFFER_CHOICE;
    const demandId = match.params.id;
    const section = demandValidation.find(x => x.section === curDemandValidation);
    const editable = section ? section.editable : false;

    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig} = useSearch();
    const [working, setWorking] = useState(true);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [realizationDialogOpen, setRealizationDialogOpen] = useState(false);
    const [cancelWhereNoOffersPopupOpen, setCancelWhereNoOffersPopupOpen] = useState(false);
    const [createPopupType, setCreatePopupType] = useState(null);

    const updateStatus = async (positionId, canceled, chosenSupplierIds, offerChoiceDate, offerChoiceJustification) => {
        setWorking(true);
        try {
            await OfferChoiceService.saveOfferChoice(demandId, positionId, canceled, chosenSupplierIds, offerChoiceDate, offerChoiceJustification);
        } catch (e) {
            setErrorDialogOpen(true);
            throw e;
        } finally {
            setWorking(false);
        }
    }

    const modifyChoice = async (positionId) => {
        setWorking(true);
        try {
            await OfferChoiceService.modifyChoice(demandId, positionId);
        } catch (e) {
            setErrorDialogOpen(true);
            throw e;
        } finally {
            setWorking(false);
        }
    }

    const refreshData = () => setSearchConfig({...searchData.search});

    useEffect(() => {
        OfferChoiceService.getChoiceJustificationBasis(demandId);
        return () => {
            OfferChoiceService.clearResources(OfferChoiceService.OFFER_CHOICE_LIST_RESOURCE_ID);
            OfferChoiceService.clearResources(OfferChoiceService.OFFER_CHOICE_JUSTIFICATIONS);
        }
    }, [demandId])

    useEffect(() => {
        setWorking(true);
        const {pagination, search} = searchData;
        OfferChoiceService.getChoiceList(demandId, pagination, search).then(() => (setWorking(false)));
        // eslint-disable-next-line
    }, [searchData, demandId])

    useDidUpdateEffect(() => {
        if (offerChoiceList?.totalCount !== undefined) {
            setTotalCount(offerChoiceList.totalCount)
        }
    }, [offerChoiceList?.totalCount]);


    const createRoundButton = useMemo(() => {
        const buttons = [];
        buttons.push(createSplitButton(Tr('additional_round_create_additional_offers_button'), () => setCreatePopupType(RoundType.ADDITIONAL_OFFERS), !editable || working));
        buttons.push(createSplitButton(Tr('additional_round_create_auction_button'), () => setCreatePopupType(RoundType.AUCTION), !editable || working));
        return (<SplitButton
            label={Tr('create_round')}
            buttons={buttons}
        />)
    }, [editable, working])

    const additionalActionsButton = useMemo(() => {
        const buttons = [];
        buttons.push(createSplitButton(Tr('invalidate_parts_no_offers'), () => setCancelWhereNoOffersPopupOpen(true), !editable || working));
        buttons.push(createSplitButton(Tr('realization_value_update'), () => setRealizationDialogOpen(true), working));
        return (<SplitButton
            label={Tr('additional_actions')}
            buttons={buttons}
            variant="outlined"
        />)
    }, [editable, working])

    if (!offerChoiceList || !offerChoiceJustification) return <Loading/>;

    return (<GridBlock column>
        <DemandValidationMessages editable={editable} section={section}/>
        <GridBlock justify='space-between'>
            <OfferChoiceSearch
                searchInProgress={working}
                doSearch={setSearchConfig}
            />
            <MultiButton buttons={[]} before={createRoundButton} after={additionalActionsButton} />
        </GridBlock>
        {!offerChoiceList?.items.length ?
            <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
            :
            <>
                <OfferChoiceTable
                    demandId={demandId}
                    updateStatus={updateStatus}
                    modifyChoice={modifyChoice}
                    offerChoiceList={offerChoiceList}
                    working={working}
                    editable={editable}
                />
                <Pagination config={searchData.pagination} setConfig={setPaginationConfig} totalCount={totalCount} disabled={working}/>
                <Alert confirmText={Tr('unknown_error_message')} open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}/>
            </>}
        {
            cancelWhereNoOffersPopupOpen &&
            <OfferChoiceCancelWhereNoOffersPopup
                demandId={demandId}
                onCancel={() => setCancelWhereNoOffersPopupOpen(false)}
                onSuccess={() => {
                    refreshData();
                    setCancelWhereNoOffersPopupOpen(false);
                }}
            />
        }
        {
            !!createPopupType &&
            <OfferChoiceCreateAdditionalRoundPopup
                demandId={demandId}
                onCancel={() => setCreatePopupType(null)}
                onSuccess={() => {
                    refreshData();
                    setCreatePopupType(null);
                }}
                roundType={createPopupType}
            />
        }
        {
            !!realizationDialogOpen &&
            <FinalRealizationValuePopup
                demandId={demandId}
                onCancel={() => setRealizationDialogOpen(false)}
                onSuccess={() => {
                    refreshData();
                    setRealizationDialogOpen(false);
                }}
                editable={editable}
            />
        }
    </GridBlock>)
}

export default connect((state) => ({
    offerChoiceList: state.resources[OfferChoiceService.OFFER_CHOICE_LIST_RESOURCE_ID],
    offerChoiceJustification: state.resources[OfferChoiceService.OFFER_CHOICE_JUSTIFICATIONS],
    demandValidation: state.resources.demandValidation,
}), {})(withRouter(OfferChoice));

